import { getLocaleCodeByCountryCode } from '@zep-us/i18n';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useLocale } from '@/hooks';
import { useCountryCode } from '@/shared/hooks';

const DEFAULT_INQUIRY_URL = 'https://tally.so/r/wvLX7X';

/** 해당 언어에 폼이 없으면 영문 폼으로 폴백한다. */
const FALLBACK_LOCALE = 'en';

/**
 * fallbackLng 가 false 라서 값이 비면 t() 가 키 문자열을 그대로 돌려준다.
 * 깨진 상대 경로로 열리지 않도록 절대 URL 만 통과시킨다.
 */
const toUrl = (value: string) => {
  const url = value.trim();
  return url.startsWith('http') ? url : undefined;
};

interface UseInquiryUrlOptions {
  /** KR일 때 사용할 내부 URL (예: '/pricing', '/embed/sales?type=inquiry') */
  krUrl?: string;
  /** 외부에서 countryCode를 주입 (제공하지 않으면 useCountryCode에서 가져옴) */
  countryCode?: string;
  /**
   * 문의 폼 종류.
   * - 'default': 기존 공통 문의 링크 (`common:paypal.help`)
   * - 'group': 학교·단체 전용 문의 폼 (`pricing:groupPricing.default.inquiryUrl`)
   */
  source?: 'default' | 'group';
}

/**
 * 문의 URL을 여는 훅
 * - KR + krUrl 제공: 내부 라우팅 (router.push)
 * - 그 외: 시트에서 URL을 가져와 새 탭으로 열기
 *
 * 학교·단체 문의(source: 'group')는 표시 언어의 폼을 쓰고,
 * 해당 언어에 값이 없으면 영문 폼으로 폴백한다.
 */
export function useInquiryUrl({
  krUrl = '/pricing',
  countryCode: externalCountryCode,
  source = 'default',
}: UseInquiryUrlOptions) {
  const router = useRouter();
  const { countryCode: userCountryCode, isKR: userIsKR } = useCountryCode();
  const { locale } = useLocale();
  const { i18n } = useTranslation(['common', 'pricing']);

  const countryCode = externalCountryCode ?? userCountryCode;
  const isKR = externalCountryCode ? externalCountryCode === 'KR' : userIsKR;

  const openInquiryUrl = async () => {
    if (isKR) {
      void router.push(krUrl);
      return;
    }

    if (source === 'group') {
      try {
        await i18n.reloadResources([locale, FALLBACK_LOCALE], 'pricing');
      } catch {
        // 리소스 갱신에 실패해도 이미 로드된 값으로 시도한다.
      }

      const groupInquiryUrl =
        toUrl(
          i18n.t('pricing:groupPricing.default.inquiryUrl', { lng: locale }),
        ) ??
        toUrl(
          i18n.t('pricing:groupPricing.default.inquiryUrl', {
            lng: FALLBACK_LOCALE,
          }),
        ) ??
        DEFAULT_INQUIRY_URL;

      window.open(groupInquiryUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const inquiryLocale = getLocaleCodeByCountryCode(countryCode);
      await i18n.reloadResources(inquiryLocale, 'common');
      const inquiryUrl = i18n.t('common:paypal.help');

      if (!inquiryUrl) {
        window.open(DEFAULT_INQUIRY_URL, '_blank', 'noopener,noreferrer');
        return;
      }

      window.open(inquiryUrl, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(DEFAULT_INQUIRY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return { openInquiryUrl };
}
